import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Play, CheckCircle, AlertCircle, RefreshCw, FileText, Clock, Trash2, Calendar, Brain, Zap } from 'lucide-react';
import { beesClient, Playbook } from '../../api/beesClient.js';
import { useChatStore } from '../../stores/useChatStore.js';


const PlaybooksPanel: React.FC = () => {
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [selectedPlaybook, setSelectedPlaybook] = useState<string>('');
  const [inputs, setInputs] = useState<Record<string, string | number | boolean | null>>({});
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, unknown> | null>(null);
  const [schedule, setSchedule] = useState<string>('');
  const [schedules, setSchedules] = useState<Record<string, string>>({});
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [showScheduleManager, setShowScheduleManager] = useState(false);
  const [plan, setPlan] = useState<{summary: string, confidence: number, stepCount: number, playbookName: string, description: string} | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  const fetchPlaybooks = async () => {
    try {
      setError(null);
      const response = await beesClient.runPlaybooks({ list: true });
      
      if (response.success && response.data && 'playbooks' in response.data) {
        setPlaybooks(response.data.playbooks);
      } else {
        setError('Failed to load playbooks');
        setPlaybooks([]);
      }
    } catch (err) {
      console.error('Failed to fetch playbooks:', err);
      setError('Failed to load playbooks');
      setPlaybooks([]);
    } finally {
      setLoading(false);
    }
  };

  const runPlaybook = async () => {
    if (!selectedPlaybook || running) return;

    setRunning(true);
    setError(null);
    setResults(null);
    
    try {
      const payload: { playbook: string; inputs: Record<string, string | number | boolean | null>; schedule?: string } = { 
        playbook: selectedPlaybook,
        inputs
      };
      
      if (schedule.trim()) {
        payload.schedule = schedule.trim();
      }
      
      console.log('🚀 Sending playbook payload:', JSON.stringify(payload, null, 2));
      console.log('📋 Current inputs state:', inputs);
      console.log('📝 Selected playbook:', selectedPlaybook);
      
      const response = await beesClient.runPlaybooks(payload);
      
      if (response.success) {
        setResults(response.data as Record<string, unknown>);
        if (schedule.trim()) {
          fetchSchedules();
          setSchedule('');
        }
      } else {
        setError(`Failed to run playbook: ${response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to run playbook:', err);
      setError('Failed to run playbook');
    } finally {
      setRunning(false);
    }
  };

  const handleInputChange = (inputName: string, value: string | number | boolean | null) => {
    setInputs(prev => ({
      ...prev,
      [inputName]: value
    }));
  };

  const getSelectedPlaybookData = useCallback(() => {
    return playbooks.find(p => p.name === selectedPlaybook);
  }, [playbooks, selectedPlaybook]);

  const fetchSchedules = async () => {
    setSchedulesLoading(true);
    try {
      const response = await beesClient.getPlaybookSchedules();
      if (response.success && response.data) {
        setSchedules(response.data.schedules);
      } else {
        console.error('Failed to fetch schedules:', response.error);
      }
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
    } finally {
      setSchedulesLoading(false);
    }
  };

  const removeSchedule = async (playbookName: string) => {
    try {
      const response = await beesClient.removePlaybookSchedule(playbookName);
      if (response.success) {
        fetchSchedules();
      } else {
        setError(`Failed to remove schedule: ${response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to remove schedule:', err);
      setError('Failed to remove schedule');
    }
  };

  const validateCronExpression = (cron: string): boolean => {
    if (!cron.trim()) return true;
    const parts = cron.trim().split(' ');
    return parts.length === 5;
  };

  const getCronDescription = (cron: string): string => {
    if (!cron) return '';
    const parts = cron.split(' ');
    if (parts.length !== 5) return 'Invalid cron expression';
    
    const [minute, hour, day, month, weekday] = parts;
    
    if (cron === '0 2 * * *') return 'Daily at 2:00 AM';
    if (cron === '*/5 * * * *') return 'Every 5 minutes';
    if (cron === '0 0 * * 0') return 'Weekly on Sunday at midnight';
    if (cron === '0 0 1 * *') return 'Monthly on the 1st at midnight';
    
    return `${minute} ${hour} ${day} ${month} ${weekday}`;
  };

  const { beeControlMode } = useChatStore();

  const onAutoAnalyze = async () => {
    if (!selectedPlaybook || analyzing) return;
    
    setAnalyzing(true);
    setError(null);
    setPlan(null);
    
    try {
      const response = await beesClient.runPlaybooks({ 
        action: 'analyze', 
        playbook: selectedPlaybook, 
        inputs 
      });
      
      if (response.success && response.data && 'planSummary' in response.data) {
        setPlan({
          summary: response.data.planSummary,
          confidence: response.data.confidence,
          stepCount: response.data.stepCount,
          playbookName: response.data.playbookName,
          description: response.data.description
        });
      } else {
        setError(`Failed to analyze playbook: ${response.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to analyze playbook:', err);
      setError('Failed to analyze playbook');
    } finally {
      setAnalyzing(false);
    }
  };

  const onAutoExecute = async () => {
    setPlan(null);
    await runPlaybook();
  };

  const handleRunPlaybook = async () => {
    if (beeControlMode === 'full') {
      await onAutoAnalyze();
    } else {
      await runPlaybook();
    }
  };

  useEffect(() => {
    fetchPlaybooks();
    fetchSchedules();
  }, []);

  useEffect(() => {
    const playbook = getSelectedPlaybookData();
    const defaultInputs: Record<string, string | number | boolean | null> = {};
    
    if (playbook?.inputs) {
      playbook.inputs.forEach((input: { name: string; default?: unknown; options?: string[]; description?: string; type?: string; required?: boolean }) => {
        if (input.default !== undefined) {
          defaultInputs[input.name] = input.default as string | number | boolean | null;
        } else if (input.options && input.options.length > 0) {
          defaultInputs[input.name] = input.options[0];
        }
      });
    }
    
    setInputs(defaultInputs);
    setResults(null);
    setError(null);
    setPlan(null);
    setSchedule('');
  }, [selectedPlaybook, getSelectedPlaybookData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
      </div>
    );
  }

  if (error && playbooks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <div className="text-red-400 text-lg">⚠️ {error}</div>
        <button 
          onClick={fetchPlaybooks} 
          className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-red-400 text-sm font-medium transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 border-b border-white/10"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg">
              <BookOpen size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Playbooks</h1>
              <p className="text-slate-400 text-sm">
                Automated workflow execution and management
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowScheduleManager(!showScheduleManager)}
              className={`p-2 border border-slate-600/30 rounded-lg text-slate-300 transition-colors ${
                showScheduleManager 
                  ? 'bg-blue-600/50 hover:bg-blue-600/60' 
                  : 'bg-slate-700/50 hover:bg-slate-600/50'
              }`}
              title="Manage Schedules"
            >
              <Calendar size={16} />
            </button>
            <button
              onClick={() => {
                fetchPlaybooks();
                fetchSchedules();
              }}
              className="p-2 bg-slate-700/50 hover:bg-slate-600/50 border border-slate-600/30 rounded-lg text-slate-300 transition-colors"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </motion.div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Schedule Manager */}
        {showScheduleManager && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6"
          >
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Calendar size={20} className="mr-2 text-blue-400" />
              Scheduled Playbooks
            </h2>
            
            {schedulesLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
              </div>
            ) : Object.keys(schedules).length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Clock size={48} className="mx-auto mb-4 opacity-50" />
                <p>No scheduled playbooks found</p>
                <p className="text-sm mt-1">Schedule a playbook below to see it here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(schedules).map(([playbookName, cronExpression]) => (
                  <div
                    key={playbookName}
                    className="flex items-center justify-between p-4 bg-slate-700/20 border border-slate-600/20 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-white">{playbookName}</div>
                      <div className="text-sm text-slate-400 mt-1">
                        <span className="font-mono text-xs bg-slate-600/30 px-2 py-1 rounded mr-2">
                          {cronExpression}
                        </span>
                        {getCronDescription(cronExpression)}
                      </div>
                    </div>
                    <button
                      onClick={() => removeSchedule(playbookName)}
                      className="p-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-red-400 transition-colors"
                      title="Remove Schedule"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
        {/* Playbook Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6"
        >
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
            <BookOpen size={20} className="mr-2 text-green-400" />
            Select Playbook
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Available Playbooks ({playbooks.length})
              </label>
              <select
                value={selectedPlaybook}
                onChange={(e) => setSelectedPlaybook(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50"
              >
                <option value="">Choose a playbook...</option>
                {playbooks.map((playbook) => (
                  <option key={playbook.name} value={playbook.name}>
                    {playbook.name} - {playbook.description}
                  </option>
                ))}
              </select>
            </div>

            {selectedPlaybook && getSelectedPlaybookData() && (
              <div className="p-4 bg-slate-700/20 border border-slate-600/20 rounded-lg">
                <h3 className="font-medium text-white mb-2">{getSelectedPlaybookData()?.name}</h3>
                <p className="text-sm text-slate-400 mb-3">{getSelectedPlaybookData()?.description}</p>
                <div className="flex items-center space-x-4 text-xs text-slate-500">
                  <span>{getSelectedPlaybookData()?.steps.length} steps</span>
                  <span>v{getSelectedPlaybookData()?.version}</span>
                  <span>{getSelectedPlaybookData()?.inputs.length} inputs required</span>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Dynamic Input Form */}
        {selectedPlaybook && getSelectedPlaybookData()?.inputs && getSelectedPlaybookData()!.inputs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6"
          >
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FileText size={20} className="mr-2 text-blue-400" />
              Configure Inputs
            </h2>
            
            <div className="space-y-4">
              {getSelectedPlaybookData()?.inputs.map((input: { name: string; default?: unknown; options?: string[]; required?: boolean; description?: string; type?: string }) => (
                <div key={input.name}>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {input.name}
                    {input.required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  <p className="text-xs text-slate-500 mb-2">{input.description}</p>
                  
                  {input.options ? (
                    <select
                      value={(inputs[input.name] as string) || (input.default as string) || ''}
                      onChange={(e) => handleInputChange(input.name, e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                    >
                      {!input.required && <option value="">Choose an option...</option>}
                      {input.options.map((option: string) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : input.type === 'boolean' ? (
                    <div className="flex items-center space-x-3">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={input.name}
                          value="true"
                          checked={inputs[input.name] === true}
                          onChange={() => handleInputChange(input.name, true)}
                          className="mr-2 text-green-500 focus:ring-green-500"
                        />
                        <span className="text-slate-300">True</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={input.name}
                          value="false"
                          checked={inputs[input.name] === false}
                          onChange={() => handleInputChange(input.name, false)}
                          className="mr-2 text-green-500 focus:ring-green-500"
                        />
                        <span className="text-slate-300">False</span>
                      </label>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={(inputs[input.name] as string) || (input.default as string) || ''}
                      onChange={(e) => handleInputChange(input.name, e.target.value)}
                      placeholder={input.default ? `Default: ${input.default}` : ''}
                      className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50"
                    />
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Schedule Configuration */}
        {selectedPlaybook && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6"
          >
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Clock size={20} className="mr-2 text-purple-400" />
              Schedule Configuration (Optional)
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Cron Expression
                </label>
                <input
                  type="text"
                  value={schedule}
                  onChange={(e) => setSchedule(e.target.value)}
                  placeholder="*/5 * * * * (every 5 minutes)"
                  className={`w-full px-3 py-2 bg-slate-700/50 border rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 transition-colors ${
                    schedule && !validateCronExpression(schedule)
                      ? 'border-red-500/50 focus:ring-red-500/50 focus:border-red-500/50'
                      : 'border-slate-600/30 focus:ring-purple-500/50 focus:border-purple-500/50'
                  }`}
                />
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-slate-500">
                    Format: minute hour day month weekday (e.g., "0 2 * * *" for daily at 2 AM)
                  </p>
                  {schedule && validateCronExpression(schedule) && (
                    <p className="text-xs text-purple-400">
                      📅 {getCronDescription(schedule)}
                    </p>
                  )}
                  {schedule && !validateCronExpression(schedule) && (
                    <p className="text-xs text-red-400">
                      ⚠️ Invalid cron expression format
                    </p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                <button
                  onClick={() => setSchedule('*/5 * * * *')}
                  className="p-2 bg-slate-600/30 hover:bg-slate-600/50 border border-slate-500/30 rounded text-slate-300 transition-colors"
                >
                  Every 5 min
                </button>
                <button
                  onClick={() => setSchedule('0 2 * * *')}
                  className="p-2 bg-slate-600/30 hover:bg-slate-600/50 border border-slate-500/30 rounded text-slate-300 transition-colors"
                >
                  Daily 2 AM
                </button>
                <button
                  onClick={() => setSchedule('0 0 * * 0')}
                  className="p-2 bg-slate-600/30 hover:bg-slate-600/50 border border-slate-500/30 rounded text-slate-300 transition-colors"
                >
                  Weekly Sunday
                </button>
                <button
                  onClick={() => setSchedule('0 0 1 * *')}
                  className="p-2 bg-slate-600/30 hover:bg-slate-600/50 border border-slate-500/30 rounded text-slate-300 transition-colors"
                >
                  Monthly 1st
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Plan Preview (Auto Mode) */}
        {plan && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-violet-900/20 to-purple-900/20 border border-violet-500/30 rounded-lg p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center">
                <Brain size={20} className="mr-2 text-violet-400" />
                Plan Preview
              </h2>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-violet-400">
                  Confidence: {(plan.confidence * 100).toFixed(0)}%
                </span>
                <div className={`w-2 h-2 rounded-full ${
                  plan.confidence >= 0.8 ? 'bg-green-400' : 
                  plan.confidence >= 0.6 ? 'bg-yellow-400' : 'bg-red-400'
                }`} />
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-slate-800/50 border border-slate-600/30 rounded-lg">
                <h3 className="font-medium text-white mb-2">{plan.playbookName}</h3>
                <p className="text-sm text-slate-400 mb-3">{plan.description}</p>
                <div className="text-xs text-slate-500">
                  {plan.stepCount} steps planned
                </div>
              </div>
              
              <div className="p-4 bg-slate-800/50 border border-slate-600/30 rounded-lg">
                <h4 className="font-medium text-white mb-2">Execution Steps:</h4>
                <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono">
                  {plan.summary}
                </pre>
              </div>
              
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setPlan(null)}
                  className="px-4 py-2 bg-slate-600/30 hover:bg-slate-600/50 border border-slate-500/30 rounded-lg text-slate-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onAutoExecute}
                  disabled={running}
                  className="px-6 py-2 bg-violet-600/20 hover:bg-violet-600/30 disabled:bg-slate-600/20 disabled:cursor-not-allowed border border-violet-500/30 rounded-lg text-violet-400 font-medium transition-colors flex items-center space-x-2"
                >
                  <Zap size={16} />
                  <span>Execute Plan</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Run Button */}
        {selectedPlaybook && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center"
          >
            <button
              onClick={handleRunPlaybook}
              disabled={running || analyzing || !selectedPlaybook || (schedule.trim() !== '' && !validateCronExpression(schedule))}
              className="px-6 py-3 bg-green-600/20 hover:bg-green-600/30 disabled:bg-slate-600/20 disabled:cursor-not-allowed border border-green-500/30 rounded-lg text-green-400 font-medium transition-colors flex items-center space-x-2"
            >
              {running ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-400"></div>
                  <span>Running...</span>
                </>
              ) : analyzing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Play size={16} />
                  <span>
                    {beeControlMode === 'full' ? 'Analyze & Run Playbook' : 
                     schedule ? 'Run & Schedule Playbook' : 'Run Playbook'}
                  </span>
                </>
              )}
            </button>
          </motion.div>
        )}

        {/* Results Display */}
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6"
          >
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <CheckCircle size={20} className="mr-2 text-green-400" />
              Execution Results
            </h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                <div className="text-green-400 font-medium">✅ Playbook executed successfully</div>
                <div className="text-sm text-slate-400 mt-1">
                  Playbook: {(results.playbook as string) || selectedPlaybook}
                </div>
              </div>

              {results.results && Array.isArray(results.results) ? (
                <div className="space-y-2">
                  <h3 className="font-medium text-white">Step-by-step Report:</h3>
                  {(results.results as Array<Record<string, unknown>>).map((step: Record<string, unknown>, index: number) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border ${
                        step.success
                          ? 'bg-green-900/20 border-green-500/30'
                          : 'bg-red-900/20 border-red-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-white">{step.step as string}</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          step.success ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {step.success ? 'SUCCESS' : 'FAILED'}
                        </span>
                      </div>
                      <div className="text-sm text-slate-400">{String(step.description || '')}</div>
                      {step.error ? (
                        <div className="text-sm text-red-400 mt-1">Error: {String(step.error)}</div>
                      ) : null}
                      {step.result ? (
                        <div className="text-xs text-slate-500 mt-1">
                          <pre className="whitespace-pre-wrap">{JSON.stringify(step.result, null, 2)}</pre>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </motion.div>
        )}

        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-900/20 border border-red-500/30 rounded-lg p-4"
          >
            <div className="text-red-400 text-sm flex items-center">
              <AlertCircle size={16} className="mr-2" />
              {error}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PlaybooksPanel;
